/**
 * Phase 6 Plan 02 — FiltersAccordion (D-132, HIST-5.5).
 *
 * Collapsible <details>/<summary> region exposing all 10 filter dimensions.
 * Per Phase 1 RTL allow-list the chevron is unmirrored static (default
 * <details> marker is visually neutral).
 *
 * Tailwind logical utilities throughout (Phase 1 D-11 + Plan 01-10 ESLint
 * rule). The trigger shows the active-count chip; the body composes
 * primitives from the existing component library.
 */
import { FormattedMessage } from "react-intl";
import { Button } from "../../../components/Button";
import type { InspectionRead } from "../../../api/inspections";
import {
  type CoatingFilter,
  type HistoryFilters,
  type Tab1Field,
  type Tab2Field,
  hasActive,
} from "./filterLogic";

interface FiltersAccordionProps {
  data: InspectionRead[];
  filters: HistoryFilters;
   
  onChange: (next: HistoryFilters) => void;
  onClearAll: () => void;
}

const TAB1_FIELDS: { key: Tab1Field; labelId: string }[] = [
  { key: "tab1_cleaned", labelId: "HIST_TICK_CLEANED" },
  { key: "tab1_pipe_blocked", labelId: "HIST_TICK_PIPE_BLOCKED" },
  { key: "tab1_structure_damaged", labelId: "HIST_TICK_STRUCTURE_DAMAGED" },
  { key: "tab1_cover_needs_level_adjustment", labelId: "HIST_TICK_COVER_LEVEL" },
];

const TAB2_FIELDS: { key: Tab2Field; labelId: string }[] = [
  { key: "tab2_repaired_internal_structure", labelId: "HIST_TICK_REPAIRED_STRUCTURE" },
  { key: "tab2_adjusted_cover_level", labelId: "HIST_TICK_ADJUSTED_COVER" },
  { key: "tab2_applied_coating", labelId: "HIST_TICK_APPLIED_COATING" },
];

const COATING_OPTIONS: { key: CoatingFilter; labelId: string }[] = [
  { key: "none", labelId: "LABEL_COATING_NONE" },
  { key: "bitumen", labelId: "LABEL_COATING_BITUMEN" },
  { key: "epoxy", labelId: "LABEL_COATING_EPOXY" },
];

function activeCount(f: HistoryFilters): number {
  let n = 0;
  if (f.worker_ids.size > 0) n++;
  if (f.date_from !== null) n++;
  if (f.date_to !== null) n++;
  if (f.tab1_ticks.size > 0) n++;
  if (f.tab2_ticks.size > 0) n++;
  if (f.coatings.size > 0) n++;
  if (f.pm_edited_only) n++;
  if (f.has_photos) n++;
  if (f.no_photos) n++;
  return n;
}

function toggleSetMember<T>(set: Set<T>, member: T): Set<T> {
  const next = new Set(set);
  if (next.has(member)) next.delete(member);
  else next.add(member);
  return next;
}

export function FiltersAccordion({
  data,
  filters,
  onChange,
  onClearAll,
}: FiltersAccordionProps) {
  const count = activeCount(filters);
  const workersById: Map<number, string> = new Map();
  for (const insp of data) {
    if (!workersById.has(insp.worker_id)) {
      workersById.set(insp.worker_id, insp.worker_name ?? `#${insp.worker_id}`);
    }
  }
  return (
    <details
      data-testid="filters-accordion"
      className="border border-divider rounded-md ps-3 pe-3 pbs-2 pbe-2"
    >
      <summary className="cursor-pointer flex items-center justify-between text-sm font-semibold text-on-surface-variant">
        <span className="inline-flex items-center gap-2">
          <FormattedMessage id="HIST_FILTERS_HEADER" />
          {count > 0 && (
            <span
              data-testid="filters-active-count"
              className="inline-flex items-center text-xs ps-2 pe-2 h-5 rounded-full bg-secondary-container text-on-secondary-container"
            >
              <FormattedMessage
                id="HIST_FILTERS_ACTIVE_COUNT"
                values={{ count }}
              />
            </span>
          )}
        </span>
      </summary>

      <div className="flex flex-col gap-4 mbs-3" data-testid="filters-body">
        {/* By worker (multi-select) */}
        <fieldset>
          <legend className="text-xs font-semibold text-on-surface-variant mbe-1">
            <FormattedMessage id="HIST_FILTER_BY_WORKER" />
          </legend>
          <div className="flex flex-wrap gap-2">
            {Array.from(workersById.entries()).map(([id, name]) => (
              <label
                key={id}
                className="inline-flex items-center gap-1 text-xs cursor-pointer"
              >
                <input
                  type="checkbox"
                  data-testid={`filter-worker-${id}`}
                  checked={filters.worker_ids.has(id)}
                  onChange={() =>
                    onChange({
                      ...filters,
                      worker_ids: toggleSetMember(filters.worker_ids, id),
                    })
                  }
                />
                <bdi>{name}</bdi>
              </label>
            ))}
          </div>
        </fieldset>

        {/* Date range */}
        <fieldset>
          <legend className="text-xs font-semibold text-on-surface-variant mbe-1">
            <FormattedMessage id="HIST_FILTER_DATE_FROM" />
          </legend>
          <input
            type="date"
            data-testid="filter-date-from"
            dir="ltr"
            value={filters.date_from ?? ""}
            onChange={(e) =>
              onChange({
                ...filters,
                date_from: e.target.value === "" ? null : e.target.value,
              })
            }
            className="h-8 bg-surface-raised border border-divider rounded-md ps-2 pe-2 text-sm"
          />
        </fieldset>
        <fieldset>
          <legend className="text-xs font-semibold text-on-surface-variant mbe-1">
            <FormattedMessage id="HIST_FILTER_DATE_TO" />
          </legend>
          <input
            type="date"
            data-testid="filter-date-to"
            dir="ltr"
            value={filters.date_to ?? ""}
            onChange={(e) =>
              onChange({
                ...filters,
                date_to: e.target.value === "" ? null : e.target.value,
              })
            }
            className="h-8 bg-surface-raised border border-divider rounded-md ps-2 pe-2 text-sm"
          />
        </fieldset>

        {/* Tab 1 ticks */}
        <fieldset>
          <legend className="text-xs font-semibold text-on-surface-variant mbe-1">
            <FormattedMessage id="HIST_FILTER_TAB1_HEADER" />
          </legend>
          <div className="flex flex-col gap-1">
            {TAB1_FIELDS.map((f) => (
              <label
                key={f.key}
                className="inline-flex items-center gap-2 text-xs cursor-pointer"
              >
                <input
                  type="checkbox"
                  data-testid={`filter-tab1-${f.key}`}
                  checked={filters.tab1_ticks.has(f.key)}
                  onChange={() =>
                    onChange({
                      ...filters,
                      tab1_ticks: toggleSetMember(filters.tab1_ticks, f.key),
                    })
                  }
                />
                <FormattedMessage id={f.labelId} />
              </label>
            ))}
          </div>
        </fieldset>

        {/* Tab 2 ticks */}
        <fieldset>
          <legend className="text-xs font-semibold text-on-surface-variant mbe-1">
            <FormattedMessage id="HIST_FILTER_TAB2_HEADER" />
          </legend>
          <div className="flex flex-col gap-1">
            {TAB2_FIELDS.map((f) => (
              <label
                key={f.key}
                className="inline-flex items-center gap-2 text-xs cursor-pointer"
              >
                <input
                  type="checkbox"
                  data-testid={`filter-tab2-${f.key}`}
                  checked={filters.tab2_ticks.has(f.key)}
                  onChange={() =>
                    onChange({
                      ...filters,
                      tab2_ticks: toggleSetMember(filters.tab2_ticks, f.key),
                    })
                  }
                />
                <FormattedMessage id={f.labelId} />
              </label>
            ))}
          </div>
        </fieldset>

        {/* Coating */}
        <fieldset>
          <legend className="text-xs font-semibold text-on-surface-variant mbe-1">
            <FormattedMessage id="HIST_FILTER_COATING_HEADER" />
          </legend>
          <div className="flex flex-wrap gap-2">
            {COATING_OPTIONS.map((c) => (
              <label
                key={c.key}
                className="inline-flex items-center gap-1 text-xs cursor-pointer"
              >
                <input
                  type="checkbox"
                  data-testid={`filter-coating-${c.key}`}
                  checked={filters.coatings.has(c.key)}
                  onChange={() =>
                    onChange({
                      ...filters,
                      coatings: toggleSetMember(filters.coatings, c.key),
                    })
                  }
                />
                <FormattedMessage id={c.labelId} />
              </label>
            ))}
          </div>
        </fieldset>

        {/* Boolean filters */}
        <fieldset>
          <div className="flex flex-col gap-1">
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                data-testid="filter-pm-edited-only"
                checked={filters.pm_edited_only}
                onChange={(e) =>
                  onChange({ ...filters, pm_edited_only: e.target.checked })
                }
              />
              <FormattedMessage id="HIST_FILTER_PM_EDITED_ONLY" />
            </label>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                data-testid="filter-has-photos"
                checked={filters.has_photos}
                onChange={(e) =>
                  onChange({ ...filters, has_photos: e.target.checked })
                }
              />
              <FormattedMessage id="HIST_FILTER_HAS_PHOTOS" />
            </label>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                data-testid="filter-no-photos"
                checked={filters.no_photos}
                onChange={(e) =>
                  onChange({ ...filters, no_photos: e.target.checked })
                }
              />
              <FormattedMessage id="HIST_FILTER_NO_PHOTOS" />
            </label>
          </div>
        </fieldset>

        {hasActive(filters) && (
          <div className="mbs-2">
            <Button
              variant="secondary"
              onClick={onClearAll}
              data-testid="filters-clear-all"
            >
              <FormattedMessage id="HIST_CLEAR_ALL_FILTERS" />
            </Button>
          </div>
        )}
      </div>
    </details>
  );
}
